import { devError } from '@/lib/logger';
import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { TrendingUp, AlertTriangle, MapPin, Clock, Hourglass, RefreshCw, UserX } from 'lucide-react';
import { format, subDays } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stable references to prevent recharts from receiving new prop objects each render
const TICK_STYLE = { fontSize: 11 };
const ABSENT_BAR_MARGIN = { left: 20 };
const ABSENT_DOMAIN = [0, 100];

export default function KPIs() {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/locations`, { withCredentials: true });
        setLocations(res.data);
      } catch (err) {
        devError('kpis-loc', err);
      }
    })();
  }, []);

  const fetchKpis = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start_date: startDate, end_date: endDate };
      if (selectedLocation !== 'all') params.location_id = selectedLocation;
      const res = await axios.get(`${API}/dashboard/kpis`, { params, withCredentials: true });
      setData(res.data);
    } catch (err) {
      devError('kpis-fetch', err);
      toast.error('Erro ao carregar KPIs');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedLocation]);

  useEffect(() => {
    fetchKpis();
  }, [fetchKpis]);

  const summary = data?.summary;
  const dailyChart = data?.daily_chart || [];
  const lateRanking = data?.late_ranking || [];
  const absenteeism = data?.absenteeism_by_location || [];
  const faltas = data?.faltas || [];

  const formatMinutes = (min) => {
    if (!min) return '0min';
    const h = Math.floor(min / 60);
    const m = min % 60;
    return h > 0 ? `${h}h${m > 0 ? `${m}min` : ''}` : `${m}min`;
  };

  return (
    <AdminLayout title="KPIs">
      {/* Filters */}
      <div className="bg-white border border-[#E5E7EB] p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider mb-2 block">Local</Label>
            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger data-testid="kpi-filter-location">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os locais</SelectItem>
                {locations.map((loc) => (
                  <SelectItem key={loc.location_id} value={loc.location_id}>
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider mb-2 block">De</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              data-testid="kpi-filter-start-date"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider mb-2 block">Até</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="kpi-filter-end-date"
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={fetchKpis}
              disabled={loading}
              data-testid="kpi-refresh-btn"
              className="bg-[#111111] text-white hover:bg-[#1F1F1F] w-full"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Carregando...' : 'Atualizar'}
            </Button>
          </div>
        </div>
      </div>

      {!summary ? (
        <div className="text-center py-16 text-[#6B7280]">Carregando KPIs...</div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <KpiCard
              icon={<Clock className="w-6 h-6" />}
              value={`${summary.total_worked_hours}h`}
              label="Horas Trabalhadas"
              testId="kpi-worked"
              color="text-[#059669]"
            />
            <KpiCard
              icon={<Hourglass className="w-6 h-6" />}
              value={`${summary.total_expected_hours}h`}
              label="Horas Esperadas"
              testId="kpi-expected"
              color="text-[#111111]"
            />
            <KpiCard
              icon={<TrendingUp className="w-6 h-6" />}
              value={`${summary.completion_pct}%`}
              label="Cumprimento"
              testId="kpi-completion"
              color={summary.completion_pct >= 95 ? 'text-[#059669]' : summary.completion_pct >= 80 ? 'text-[#F59E0B]' : 'text-[#DC2626]'}
            />
            <KpiCard
              icon={<TrendingUp className="w-6 h-6" />}
              value={`${summary.banco_horas > 0 ? '+' : ''}${summary.banco_horas}h`}
              label="Banco de Horas"
              testId="kpi-banco"
              color={summary.banco_horas >= 0 ? 'text-[#059669]' : 'text-[#DC2626]'}
            />
            <KpiCard
              icon={<UserX className="w-6 h-6" />}
              value={summary.faltas_count || 0}
              label={`Faltas (${summary.faltas_missed_hours || 0}h)`}
              testId="kpi-faltas"
              color={(summary.faltas_count || 0) === 0 ? 'text-[#059669]' : 'text-[#DC2626]'}
            />
          </div>

          {/* Daily chart */}
          <section className="bg-white border border-[#E5E7EB] p-6 mb-6" data-testid="kpi-daily-chart">
            <h2 className="text-sm uppercase tracking-widest font-bold mb-4">Horas Trabalhadas vs. Esperadas</h2>
            {dailyChart.length === 0 ? (
              <div className="py-12 text-center text-[#9CA3AF]">Sem dados no período selecionado.</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={dailyChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="date" tick={TICK_STYLE} />
                  <YAxis tick={TICK_STYLE} unit="h" />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="expected" stroke="#9CA3AF" strokeWidth={2} name="Esperadas" strokeDasharray="5 5" />
                  <Line type="monotone" dataKey="worked" stroke="#0029FF" strokeWidth={3} name="Trabalhadas" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Late ranking */}
            <section className="bg-white border border-[#E5E7EB] p-6" data-testid="kpi-late-ranking">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-5 h-5 text-[#DC2626]" />
                <h2 className="text-sm uppercase tracking-widest font-bold">Ranking de Atrasos</h2>
              </div>
              {lateRanking.length === 0 ? (
                <div className="py-8 text-center text-[#9CA3AF] text-sm">Sem atrasos registados no período. 🎉</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E5E7EB]">
                      <th className="text-left py-2 text-xs uppercase tracking-wider text-[#6B7280]">#</th>
                      <th className="text-left py-2 text-xs uppercase tracking-wider text-[#6B7280]">Funcionário</th>
                      <th className="text-right py-2 text-xs uppercase tracking-wider text-[#6B7280]">Atrasos</th>
                      <th className="text-right py-2 text-xs uppercase tracking-wider text-[#6B7280]">Tempo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lateRanking.map((emp, idx) => (
                      <tr key={emp.employee_id} className="border-b border-[#F3F4F6]" data-testid={`late-row-${idx}`}>
                        <td className="py-3 font-bold text-[#DC2626]">{idx + 1}</td>
                        <td className="py-3">{emp.name}</td>
                        <td className="py-3 text-right">{emp.late_count}</td>
                        <td className="py-3 text-right text-[#6B7280]">{formatMinutes(emp.total_late_minutes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            {/* Absenteeism */}
            <section className="bg-white border border-[#E5E7EB] p-6" data-testid="kpi-absenteeism">
              <div className="flex items-center gap-3 mb-4">
                <MapPin className="w-5 h-5 text-[#F59E0B]" />
                <h2 className="text-sm uppercase tracking-widest font-bold">Absenteísmo por Local</h2>
              </div>
              {absenteeism.length === 0 ? (
                <div className="py-8 text-center text-[#9CA3AF] text-sm">Sem dados.</div>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(220, absenteeism.length * 50)}>
                  <BarChart data={absenteeism} layout="vertical" margin={ABSENT_BAR_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis type="number" tick={TICK_STYLE} unit="%" domain={ABSENT_DOMAIN} />
                    <YAxis type="category" dataKey="name" tick={TICK_STYLE} width={120} />
                    <Tooltip formatter={(value) => `${value}%`} />
                    <Bar dataKey="rate" fill="#F59E0B" name="Taxa" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </section>
          </div>

          {/* Faltas detectadas */}
          <section className="bg-white border border-[#E5E7EB] p-6 mt-6" data-testid="kpi-faltas-list">
            <div className="flex items-center gap-3 mb-2">
              <UserX className="w-5 h-5 text-[#DC2626]" />
              <h2 className="text-sm uppercase tracking-widest font-bold">Faltas Detectadas</h2>
            </div>
            <p className="text-xs text-[#6B7280] mb-4">
              Dias com escala mas sem picagem registada (excluindo férias/faltas justificadas).
              Estas horas <strong>não são descontadas do banco de horas</strong>.
            </p>
            {faltas.length === 0 ? (
              <div className="py-6 text-center text-[#9CA3AF] text-sm">Sem faltas detectadas no período. 🎉</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E5E7EB]">
                    <th className="text-left py-2 text-xs uppercase tracking-wider text-[#6B7280]">Data</th>
                    <th className="text-left py-2 text-xs uppercase tracking-wider text-[#6B7280]">Funcionário</th>
                    <th className="text-left py-2 text-xs uppercase tracking-wider text-[#6B7280]">Local</th>
                    <th className="text-right py-2 text-xs uppercase tracking-wider text-[#6B7280]">Horas Não Trabalhadas</th>
                  </tr>
                </thead>
                <tbody>
                  {faltas.map((f) => (
                    <tr key={`${f.employee_id}-${f.date}`} className="border-b border-[#F3F4F6]" data-testid={`falta-row-${f.employee_id}-${f.date}`}>
                      <td className="py-3 mono text-[#DC2626] font-bold">{f.date}</td>
                      <td className="py-3">{f.name}</td>
                      <td className="py-3 text-[#6B7280]">{f.location_name}</td>
                      <td className="py-3 text-right mono">{f.expected_hours}h</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {summary.faltas_count > faltas.length && (
              <p className="text-xs text-[#9CA3AF] mt-3 text-center">
                Mostrando {faltas.length} de {summary.faltas_count} faltas. Refine o filtro de data para ver mais.
              </p>
            )}
          </section>

          <p className="text-xs text-[#9CA3AF] mt-6 text-center">
            {summary.records_count} registo(s) analisado(s) no período {data.range.start_date} a {data.range.end_date}.
          </p>
        </>
      )}
    </AdminLayout>
  );
}

function KpiCard({ icon, value, label, testId, color }) {
  return (
    <div className="bg-white border border-[#E5E7EB] p-4" data-testid={testId}>
      <div className={`flex items-center gap-2 mb-2 ${color || 'text-[#111111]'}`}>{icon}</div>
      <div className={`text-2xl font-black ${color || 'text-[#111111]'}`} style={{ fontFamily: 'Satoshi, sans-serif' }}>
        {value}
      </div>
      <div className="text-xs uppercase tracking-wider text-[#6B7280] mt-1">{label}</div>
    </div>
  );
}
